import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavegate } from 'App/hooks';
import { configurationsRoutes } from 'domains/Configurations';
import { DOMAIN_ORDER_PROCESSING_STATUSES } from '../../../constants';
import {
  useUpdateDomainOrderContactMutation,
  useLazyGetDomainOrderQuery,
} from '../../../domainsApi';
import { RegistrationFormValues } from '../types';

interface UseDomainCheckoutFlowProps {
  payOrderId: string;
  registrationValues: RegistrationFormValues;
  creditCardStatus: 'idle' | 'loading' | 'success' | 'error';
}

export function useDomainCheckoutFlow({
  payOrderId,
  registrationValues,
  creditCardStatus,
}: UseDomainCheckoutFlowProps) {
  const { goTo, buildPath } = useNavegate();
  const shouldExecuteFlowRef = useRef(false);
  const [isExecuting, setIsExecuting] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [updateContact] = useUpdateDomainOrderContactMutation();
  const [getDomainOrder] = useLazyGetDomainOrderQuery();

  const pollOrderStatus = useCallback(
    async (orderPayOrderId: string): Promise<void> => {
      const maxAttempts = 10;
      const intervalMs = 1000;

      const statusesToWaitFor = DOMAIN_ORDER_PROCESSING_STATUSES;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const { data } = await getDomainOrder(orderPayOrderId);

        if (data && !statusesToWaitFor.includes(data.status)) {
          goTo(
            buildPath(configurationsRoutes.domainsOrder, {
              payOrderId: orderPayOrderId,
            }),
          );
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }

      goTo(
        buildPath(configurationsRoutes.domainsOrder, {
          payOrderId: orderPayOrderId,
        }),
      );
    },
    [getDomainOrder, goTo, buildPath],
  );

  const executePostPaymentFlow = useCallback(async () => {
    setIsExecuting('loading');
    try {
      await updateContact({
        payOrderId,
        contact: {
          contact: {
            firstName: registrationValues.firstName,
            lastName: registrationValues.lastName,
            email: registrationValues.ownerEmail,
            phone: registrationValues.phone,
            address: {
              address1: registrationValues.address,
              city: registrationValues.city,
              state: registrationValues.state,
              postalCode: registrationValues.zipCode,
              country: registrationValues.country,
            },
          },
        },
      }).unwrap();

      await pollOrderStatus(payOrderId);
    } catch (error) {
      // Error is silently caught - the flow completes and resets isExecuting
      // allowing the user to retry if needed
    } finally {
      setIsExecuting('success');
    }
  }, [updateContact, payOrderId, registrationValues, pollOrderStatus]);

  useEffect(() => {
    if (shouldExecuteFlowRef.current && creditCardStatus === 'success') {
      shouldExecuteFlowRef.current = false;
      executePostPaymentFlow();
    }
  }, [creditCardStatus, executePostPaymentFlow]);

  const startFlow = useCallback(() => {
    shouldExecuteFlowRef.current = true;
  }, []);

  return {
    startFlow,
    isExecuting,
  };
}
